import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useRef, useEffect, useState, useMemo } from 'react';
import * as THREE from 'three';

// Flowing gradient mesh - silk/liquid ribbon effect
function FlowingGradientMesh({ 
  scrollY = 0 
}: { 
  scrollY?: number;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const materialRef = useRef<THREE.ShaderMaterial>(null);
  
  const shaderMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uScrollY: { value: 0 },
        uColor1: { value: new THREE.Color('#0a1628') },
        uColor2: { value: new THREE.Color('#1a2d4a') },
        uColor3: { value: new THREE.Color('#2a3a5f') },
        uColor4: { value: new THREE.Color('#1a254a') },
      },
      vertexShader: `
        varying vec2 vUv;
        varying vec3 vPosition;
        uniform float uTime;
        uniform float uScrollY;
        
        void main() {
          vUv = uv;
          vPosition = position;
          
          // Flowing wave displacement
          float wave1 = sin(position.x * 0.8 + uTime * 0.15) * 0.3;
          float wave2 = cos(position.y * 0.6 + uTime * 0.12) * 0.25;
          float wave3 = sin((position.x + position.y) * 0.4 + uTime * 0.1) * 0.2;
          
          vec3 newPosition = position;
          newPosition.z += wave1 + wave2 + wave3;
          newPosition.y += uScrollY * 0.0001;
          
          gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
        }
      `,
      fragmentShader: `
        varying vec2 vUv;
        varying vec3 vPosition;
        uniform float uTime;
        uniform vec3 uColor1;
        uniform vec3 uColor2;
        uniform vec3 uColor3;
        uniform vec3 uColor4;
        
        void main() {
          // Flowing gradient calculation
          float flow1 = sin(vUv.x * 3.0 + uTime * 0.08) * 0.5 + 0.5;
          float flow2 = cos(vUv.y * 2.5 + uTime * 0.06) * 0.5 + 0.5;
          float flow3 = sin((vUv.x + vUv.y) * 2.0 + uTime * 0.1) * 0.5 + 0.5;
          
          // Blend colors smoothly
          vec3 color = mix(uColor1, uColor2, flow1);
          color = mix(color, uColor3, flow2 * 0.6);
          color = mix(color, uColor4, flow3 * 0.4);
          
          // Soft light bloom effect
          float bloom = pow(flow1 * flow2, 2.0) * 0.15;
          color += vec3(bloom * 0.3, bloom * 0.4, bloom * 0.6);
          
          // Edge softness
          float edgeFade = smoothstep(0.0, 0.3, vUv.x) * smoothstep(1.0, 0.7, vUv.x);
          edgeFade *= smoothstep(0.0, 0.3, vUv.y) * smoothstep(1.0, 0.7, vUv.y);
          
          gl_FragColor = vec4(color, 0.85 * edgeFade);
        }
      `,
      transparent: true,
      side: THREE.DoubleSide,
    });
  }, []);
  
  useFrame(({ clock }) => {
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = clock.getElapsedTime();
      materialRef.current.uniforms.uScrollY.value = scrollY;
    }
  });

  return (
    <mesh ref={meshRef} position={[0, 0, -3]} rotation={[0.1, 0, 0]}>
      <planeGeometry args={[12, 8, 64, 48]} />
      <primitive object={shaderMaterial} ref={materialRef} attach="material" />
    </mesh>
  );
}

// Secondary flowing ribbon - adds depth
function FlowingRibbon({
  scrollY = 0,
  offset = 0,
  opacity = 0.4
}: {
  scrollY?: number;
  offset?: number;
  opacity?: number;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const materialRef = useRef<THREE.ShaderMaterial>(null);
  
  const shaderMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uOffset: { value: offset },
        uOpacity: { value: opacity },
      },
      vertexShader: `
        varying vec2 vUv;
        uniform float uTime;
        uniform float uOffset;
        
        void main() {
          vUv = uv;
          
          vec3 newPosition = position;
          
          // Silk-like flowing motion
          float wave = sin(position.x * 1.2 + uTime * 0.1 + uOffset) * 0.4;
          wave += cos(position.y * 0.8 + uTime * 0.08 + uOffset * 0.5) * 0.3;
          newPosition.z += wave;
          
          gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
        }
      `,
      fragmentShader: `
        varying vec2 vUv;
        uniform float uTime;
        uniform float uOpacity;
        
        void main() {
          // Gradient ribbon colors
          vec3 color1 = vec3(0.15, 0.25, 0.45);
          vec3 color2 = vec3(0.2, 0.3, 0.55);
          vec3 color3 = vec3(0.12, 0.2, 0.4);
          
          float flow = sin(vUv.x * 4.0 + uTime * 0.12) * 0.5 + 0.5;
          float flowY = cos(vUv.y * 3.0 + uTime * 0.09) * 0.5 + 0.5;
          
          vec3 color = mix(color1, color2, flow);
          color = mix(color, color3, flowY * 0.5);
          
          // Soft edges
          float alpha = smoothstep(0.0, 0.4, vUv.y) * smoothstep(1.0, 0.6, vUv.y);
          alpha *= smoothstep(0.0, 0.2, vUv.x) * smoothstep(1.0, 0.8, vUv.x);
          
          gl_FragColor = vec4(color, alpha * uOpacity);
        }
      `,
      transparent: true,
      side: THREE.DoubleSide,
    });
  }, [offset, opacity]);
  
  useFrame(({ clock }) => {
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = clock.getElapsedTime();
    }
    if (meshRef.current) {
      meshRef.current.position.y = scrollY * 0.0001;
    }
  });

  return (
    <mesh ref={meshRef} position={[offset * 2, offset, -4 - offset]} rotation={[0.15 + offset * 0.1, offset * 0.1, 0]}>
      <planeGeometry args={[8, 5, 48, 32]} />
      <primitive object={shaderMaterial} ref={materialRef} attach="material" />
    </mesh>
  );
}

// Soft light bloom spots
function LightBloom({ position, scale = 1, color = '#2a4a7a' }: { 
  position: [number, number, number]; 
  scale?: number;
  color?: string;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  
  useFrame(({ clock }) => {
    if (meshRef.current) {
      const t = clock.getElapsedTime() * 0.05;
      meshRef.current.scale.setScalar(scale * (1 + Math.sin(t) * 0.1));
    }
  });

  return (
    <mesh ref={meshRef} position={position}>
      <sphereGeometry args={[1, 32, 32]} />
      <meshBasicMaterial 
        color={color}
        transparent
        opacity={0.08}
      />
    </mesh>
  );
}

// Main scene
function Scene({ scrollY }: { scrollY: number }) {
  const { camera } = useThree();
  
  // Subtle camera drift
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime() * 0.02;
    camera.position.x = Math.sin(t) * 0.05;
    camera.position.y = Math.cos(t * 0.7) * 0.03;
  });

  return (
    <>
      {/* Main flowing gradient surface */}
      <FlowingGradientMesh scrollY={scrollY} />
      
      {/* Layered flowing ribbons for depth */}
      <FlowingRibbon scrollY={scrollY} offset={-0.5} opacity={0.3} />
      <FlowingRibbon scrollY={scrollY} offset={0.5} opacity={0.25} />
      <FlowingRibbon scrollY={scrollY} offset={-1} opacity={0.2} />
      
      {/* Soft light blooms */}
      <LightBloom position={[-3, 1, -5]} scale={2} color="#1a3a6a" />
      <LightBloom position={[3, -0.5, -6]} scale={2.5} color="#2a4a7a" />
      <LightBloom position={[0, 2, -7]} scale={1.8} color="#1a2a5a" />
    </>
  );
}

// Main export
export function Hero3DOverlay({ scrollY = 0 }: { scrollY?: number }) {
  const [reducedMotion, setReducedMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [webglSupported, setWebglSupported] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mediaQuery.matches);
    
    const handleChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    
    setIsMobile(window.innerWidth < 768);
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      setWebglSupported(!!gl);
    } catch {
      setWebglSupported(false);
    }
    
    const timer = setTimeout(() => setIsLoaded(true), 150);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, []);

  if (reducedMotion || isMobile || !webglSupported) {
    return null;
  }

  return (
    <div 
      className="absolute inset-0 z-[1] pointer-events-none transition-opacity duration-500"
      style={{ opacity: isLoaded ? 1 : 0 }}
    >
      <Canvas
        camera={{ position: [0, 0, 5], fov: 55 }}
        dpr={[1, 1.5]}
        gl={{ 
          antialias: false,
          alpha: true,
          powerPreference: 'high-performance',
          preserveDrawingBuffer: false,
          failIfMajorPerformanceCaveat: false,
        }}
        frameloop="demand"
        style={{ background: 'transparent' }}
      >
        <Scene scrollY={scrollY} />
      </Canvas>
    </div>
  );
}
